import { Injectable, Inject } from '@angular/core';
import { BehaviorSubject, firstValueFrom } from 'rxjs';
import { OepaIopAuthLibService } from '@ohioepa/oepa-iop-auth-lib';
import { UserService } from '../cems/cems-admin/users/user.service';
import { environment } from '../../environments/environment';

export interface UserState {
  ohioId: string | null;
  isAdmin: boolean;
  isActive: boolean;
}

@Injectable({ providedIn: 'root' })
export class UserStateService {
  private userStateSubject: BehaviorSubject<UserState>;
  userState$;

  private developmentMode: boolean = environment.redirectURI.includes('localhost');

  constructor(
    @Inject(OepaIopAuthLibService) private authService: OepaIopAuthLibService,
    @Inject(UserService) private userService: UserService
  ) {
    // Restore user state from sessionStorage if present
    const saved = sessionStorage.getItem('userState');
    let initialState: UserState = { ohioId: null, isAdmin: false, isActive: false };
    if (saved) {
      try {
        initialState = JSON.parse(saved);
      } catch (e) {
        // ignore parse error, use default
      }
    }
    this.userStateSubject = new BehaviorSubject<UserState>(initialState);
    this.userState$ = this.userStateSubject.asObservable();
  }


  setUserState(ohioId: string | null, isAdmin: boolean, isActive: boolean) {
    const state = { ohioId, isAdmin, isActive };
    sessionStorage.setItem('userState', JSON.stringify(state));
    this.userStateSubject.next(state);
  }

  /**
   * Loads user state asynchronously: waits for authentication and privilege info.
   * Sets user state or clears it if not authenticated.
   */
  async loadUserState(): Promise<void> {
    if (this.developmentMode) {
      this.setUserState(environment.ohioId, true, true);
      return;
    }
    try {
      
      const profile = await firstValueFrom(this.authService.userProfile$);
      if (profile && profile.eidmuserid) {
        const userResp = await firstValueFrom(this.userService.getUserByOhioId(profile.eidmuserid));
        const isAdmin = !!(userResp && userResp.data && userResp.data.isAdmin);
        const isActive = !!(userResp && userResp.data && userResp.data.isActive);
        this.setUserState(profile.eidmuserid, isAdmin, isActive);
      } else if (profile === null) {
        this.clearUserState();
      }
    } catch (err) {
      this.clearUserState();
    }
  }

  clearUserState() {
    sessionStorage.removeItem('userState');
    this.userStateSubject.next({ ohioId: null, isAdmin: false, isActive: false });
  }

  getCurrentUserState(): UserState {
    return this.userStateSubject.getValue();
  }
}
